import { useCallback } from 'react'
import { RestaurantArea } from '~/components/area/restaurantArea.frag'
import { RestaurantBreadcrumbs } from '~/components/breadcrumbs/RestaurantBreadcrumbs.frag'
import { MealPeriodsCalendarSection } from '~/components/calendar/sd/MealPeriodsCalendarSection'
import { SdFooter } from '~/components/footer/sd'
import { RestaurantGenre } from '~/components/genre/restaurantGenre.frag'
import { CouponBanner } from '~/features/coupon/CouponBanner'
import { Announcement } from '~/features/guide/Announcement'
import type { RestaurantSummaryType } from '~/features/guide/getRestaurantSummary.graphql'
import { useSetReservable } from '~/features/guide/hooks/reservable'
import { GuideFooter } from '~/features/guide/sd/GuideFooter'
import { SdGuideTitleImage } from '~/features/photos/GuideTitleImage'
import { RestaurantPhotos } from '~/features/photos/RestaurantPhotos.frag'
import { ImageThumbnail } from '~/features/photos/sd/ImageThumbnail'
import { buildRestaurantBreadcrumbs } from '~/lib/breadcrumbs/breadcrumbs'
import { getFragmentData } from '~/lib/graphql'
import type { RestaurantId } from '~/lib/graphql/scalars'
import type { GuideQuery } from '~/lib/query/guide'
import { updateGuideTitle } from '~/lib/seo/guide'
import { vstack } from '~/styled-system/patterns'
import { Breadcrumbs } from '~/ui/Breadcrumbs'
import { GuideIkyuDayBanner } from '../GuideIkyuDayBanner'
import { GuideNav } from '../GuideNav'
import { GuideTitle } from '../GuideTitle'
import { Introduction } from '../Introduction'
import InventoryNotificationComponent from '../InventoryNotification'
import { IkyuOnlyKiwaminoArticles } from '../KiwaminoArticles'
import { NearbySpots } from '../NearbySpots'
import { RestaurantSummary } from '../RestaurantSummary.frag'
import SimilarRestaurants from '../SimilarRestaurants'
import { IkyuOnlySupremeRestaurantBanner } from '../SupremeRestaurantBanner'
import { CourseSection } from '../courses/CourseSection'
import { IkyuOnlyOtoriyoseProducts } from '../otoriyose/OtoriyoseProducts'
import { RestaurantAreaReviewRankingFragment } from '../restaurantAreaReviewRanking.frag'
import { RestaurantRating } from '../restaurantRating.frag'
import { GuideRestaurantInfo } from './GuideRestaurantInfo'
import { GuideReviewSummary } from './GuideReviewSummary'
import { NearbyRestaurants } from './NearbyRestaurants'
import { PpgOnlyPolicies } from './Policies'
import { RecommendRestaurants } from './RecommendRestaurants'
import { RestaurantFaq } from './RestaurantFaq'

type Props = {
  restaurantId: RestaurantId
  query: GuideQuery
  restaurant: RestaurantSummaryType
  showIkyuDayBanner: boolean
}

export function SDGuide({ restaurantId, query, restaurant, showIkyuDayBanner }: Props) {
  const restaurantSummary = getFragmentData(RestaurantSummary, restaurant)

  const ratingInfo = getFragmentData(RestaurantRating, restaurantSummary)
  const areaReviewRanking = getFragmentData(
    RestaurantAreaReviewRankingFragment,
    restaurantSummary.areaReviewRanking
  )
  const area = getFragmentData(RestaurantArea, restaurantSummary)
  const genre = getFragmentData(RestaurantGenre, restaurantSummary)

  const visibleTimeSaleLabel = restaurant.searchCourses.nodes
    .flatMap((course) => course.searchPlans.nodes)
    .some((plan) => plan.timesale?.visibleLabel)

  const breadcrumbsData = getFragmentData(RestaurantBreadcrumbs, restaurant)
  const breadcrumbs = buildRestaurantBreadcrumbs(
    breadcrumbsData.name,
    restaurantId,
    breadcrumbsData.area,
    breadcrumbsData.genre,
    true
  )

  const setReservable = useSetReservable(restaurantId)

  const handleHasInventory = useCallback(
    (hasInventory: boolean) => {
      setReservable(hasInventory)
      if (hasInventory) {
        updateGuideTitle()
      }
    },
    [setReservable]
  )

  const { images } = getFragmentData(RestaurantPhotos, restaurant)

  // 横スクロールのあるコンポーネントはスクロール位置が維持されてしまうのでそれを回避するため key を設定する
  // ガイド間遷移でコンポーネントが再利用されてしまうので useEffect の cleanup は走らないので cleanup で対処できない
  return (
    <>
      <CouponBanner restaurantId={restaurantId} size="narrow" />
      <GuideIkyuDayBanner show={showIkyuDayBanner} largeAreaCode={area.area.largeCode} />
      <GuideTitle restaurant={restaurant} visibleTimeSaleLabel={visibleTimeSaleLabel} />
      <SdGuideTitleImage restaurant={restaurant} />
      <GuideNav restaurantId={restaurantId} rating={ratingInfo.rating} restaurant={restaurant} />
      <GuideReviewSummary
        rating={ratingInfo.rating}
        areaReviewRanking={areaReviewRanking}
        restaurantId={restaurantId as RestaurantId}
        isReviewHidden={restaurantSummary.isReviewHidden}
      />
      <Introduction restaurant={restaurant} />
      <div className="divide-y-8 divide-neutral-200">
        <div>
          <MealPeriodsCalendarSection
            query={query}
            restaurantId={restaurantId}
            restaurant={restaurant}
            showIkyuDayBalloon={showIkyuDayBanner}
            onHasInventory={handleHasInventory}
          />
          {restaurantSummary.supportsInventoryNotification && (
            <div className="px-3 pb-2">
              <InventoryNotificationComponent withSpread restaurantId={restaurantId} />
            </div>
          )}
        </div>
        {/* PPG は画像のない店舗もあるので、ないときは表示しない */}
        {images.length > 0 && (
          <div className="space-y-5 pb-3">
            <ImageThumbnail
              restaurantId={restaurantId}
              restaurant={restaurant}
              // key を設定している理由は上記コメント参照
              key={`image-thumbnail-${restaurantId}`}
            />
            <IkyuOnlyKiwaminoArticles />
          </div>
        )}
        <CourseSection restaurantId={restaurantId} query={query} />
        <Announcement restaurant={restaurant} />
        <GuideRestaurantInfo restaurant={restaurant} />
        <div className="divide-y divide-neutral-200">
          <RestaurantFaq restaurant={restaurant} />
          <PpgOnlyPolicies restaurantId={restaurantId} />
          <IkyuOnlyOtoriyoseProducts />
          <IkyuOnlySupremeRestaurantBanner restaurant={restaurant} size="narrow" />
          <div>
            <RecommendRestaurants
              restaurantId={restaurantId}
              key={`recommend-restaurants-${restaurantId}`}
            />
            <NearbyRestaurants
              area={area}
              restaurant={restaurant}
              // key を設定している理由は上記コメント参照
              key={`nearby-restaurants-${restaurantId}`}
            />
            <div className={vstack({ gap: '5', py: '3' })}>
              <SimilarRestaurants area={area} genre={genre} restaurant={restaurant} />
              <NearbySpots restaurant={restaurant} />
            </div>
          </div>
        </div>
      </div>
      <div
        className="overflow-scroll whitespace-nowrap bg-neutral-100 py-2 pl-3"
        // key を設定している理由は上記コメント参照
        key={`breadcrumbs-${restaurantId}`}
      >
        <Breadcrumbs breadcrumbs={breadcrumbs} />
      </div>
      <SdFooter />
      <GuideFooter
        supportsInventoryNotification={restaurantSummary.supportsInventoryNotification}
        restaurantId={restaurantId}
      />
    </>
  )
}
